import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';

gsap.registerPlugin(MotionPathPlugin);

class CircularTextAnimation {
  constructor(containerSelector, options = {}) {
    this.container = document.querySelector(containerSelector);
    if (!this.container) return;
    
    this.texts = [];
    this.timeline = null;
    this.resizeObserver = null;
    this.svgElement = null;
    this.pathElement = null;
    this.pathId = `circular-path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // オプションのデフォルト値
    this.options = {
      textContent: options.textContent || 'YOUR TEXT HERE',
      textCount: options.textCount || 4, // テキスト数（増やすとギャップが埋まる）
      textGap: options.textGap || '　　　', // テキスト間の余白（全角スペース等）
      duration: options.duration || 10, // アニメーション速度（秒）
      fontSize: options.fontSize || 'clamp(14px, 2vw, 18px)',
      fontWeight: options.fontWeight || 'bold',
      color: options.color || '#333',
      offsetY: options.offsetY || 20,
      ...options
    };
    
    this.init();
  }
  
  init() {
    this.createSVGPath();
    this.createTexts();
    this.setupAnimation();
    this.setupResizeObserver();
  }
  
  createSVGPath() {
    if (this.svgElement) {
      this.svgElement.remove();
    }
    
    this.svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    this.svgElement.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      opacity: 1;
      z-index: 5;
    `;
    
    this.pathElement = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    this.pathElement.setAttribute('id', this.pathId);
    this.pathElement.setAttribute('fill', 'none');
    this.pathElement.setAttribute('stroke', 'red');
    this.pathElement.setAttribute('stroke-width', '2');
    
    this.svgElement.appendChild(this.pathElement);
    this.container.appendChild(this.svgElement);
  }
  
  updatePath() {
    const separatorBottom = document.querySelector('.p-articleMessage__separatorBottom');
    if (!separatorBottom) return;
    
    const separatorRect = separatorBottom.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();
    
    const offsetTop = separatorRect.top - containerRect.top;
    const separatorWidth = separatorRect.width;
    const separatorHeight = separatorRect.height;
    
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const isTablet = window.matchMedia('(min-width: 769px) and (max-width: 1024px)').matches;
    
    let maskScale;
    if (isMobile) {
      maskScale = 3.5;
    } else if (isTablet) {
      maskScale = 1.8;
    } else {
      maskScale = 2.35;
    }
    
    const svgOriginalWidth = 2077;
    const svgOriginalHeight = 1082;
    
    const maskWidth = separatorWidth * maskScale;
    const maskHeight = svgOriginalHeight * (maskWidth / svgOriginalWidth);
    
    const maskLeft = (separatorWidth - maskWidth) / 2;
    const maskTop = separatorHeight - maskHeight;
    
    const circleCenterInSVG = { x: svgOriginalWidth / 2, y: 28.5 };
    
    const radius = 1038.5 * (maskWidth / svgOriginalWidth);
    const outerRadius = radius + this.options.offsetY;
    
    const centerX = maskLeft + circleCenterInSVG.x * (maskWidth / svgOriginalWidth);
    const centerY = offsetTop + maskTop + circleCenterInSVG.y * (maskHeight / svgOriginalHeight);
    
    const svgWidth = separatorWidth;
    const svgHeight = Math.max(separatorHeight, offsetTop + separatorHeight + 200);
    
    this.svgElement.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
    this.svgElement.setAttribute('width', svgWidth);
    this.svgElement.setAttribute('height', svgHeight);
    
    const pathData = this.createVisibleArcPath(
      centerX,
      centerY,
      outerRadius,
      svgWidth,
      svgHeight
    );
    
    this.pathElement.setAttribute('d', pathData);
  }

  createVisibleArcPath(cx, cy, radius, containerWidth, containerHeight) {
    const points = [];
    const steps = 300;
    
    for (let i = 0; i <= steps; i++) {
      const angle = -210 + (420 * i / steps);
      const radian = (angle * Math.PI) / 180;
      
      const x = cx + radius * Math.cos(radian);
      const y = cy + radius * Math.sin(radian);
      
      if (x >= -50 && x <= containerWidth + 50) {
        points.push({ x, y });
      }
    }
    
    if (points.length === 0) return 'M 0 0';
    
    return points.map((point, i) => 
      `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ');
  }
  
  createTexts() {
    for (let i = 0; i < this.options.textCount; i++) {
      const textEl = document.createElement('div');
      textEl.className = 'circular-text';
      // テキストの後にギャップを追加
      textEl.textContent = this.options.textContent + this.options.textGap;
      textEl.style.cssText = `
        position: absolute;
        white-space: nowrap;
        font-size: ${this.options.fontSize};
        font-weight: ${this.options.fontWeight};
        color: ${this.options.color};
        pointer-events: none;
        background: yellow;
        padding: 5px;
        z-index: 999;
      `;
      this.container.appendChild(textEl);
      this.texts.push(textEl);
    }
  }
  
  setupAnimation() {
    if (this.timeline) {
      this.timeline.kill();
    }
    
    this.updatePath();
    
    this.timeline = gsap.timeline({ repeat: -1 });
    
    this.texts.forEach((text, index) => {
      const offset = index / this.texts.length;
      
      this.timeline.to(text, {
        duration: this.options.duration,
        ease: 'none',
        motionPath: {
          path: `#${this.pathId}`,
          autoRotate: true,
          alignOrigin: [0.5, 0.5],
          start: offset,
          end: offset + 1
        }
      }, 0);
    });
  }
  
  setupResizeObserver() {
    let resizeTimer;
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.setupAnimation();
      }, 250);
    });
    
    this.resizeObserver.observe(this.container);
  }
  
  destroy() {
    if (this.timeline) {
      this.timeline.kill();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.svgElement) {
      this.svgElement.remove();
    }
    this.texts.forEach(text => text.remove());
  }
}

export { CircularTextAnimation };
